import { useTranslation } from 'react-i18next';
import { EyeIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';

type ProcurosOnlyBadgeProps = {
  label?: string;
  /** The badge's own background: 'white' for use on an orange surface, 'orange' everywhere else. */
  variant?: 'orange' | 'white';
  className?: string;
};

const VARIANT_CLASSES = {
  orange: 'bg-orange-50',
  white: 'bg-white',
} as const;

/**
 * Marks something as visible to Procuros members only. Never put it on content the API also returns
 * to non-admin users — the whole point is that the customer does not see whatever it is attached to.
 */
export const ProcurosOnlyBadge = ({ label, variant = 'orange', className }: ProcurosOnlyBadgeProps) => {
  const { t } = useTranslation();

  const procurosOnlyText = t('common:components.procurosOnlyBadge.label');

  const badge = (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs leading-4 font-medium whitespace-nowrap text-orange-800',
        VARIANT_CLASSES[variant],
        className,
      )}
    >
      <EyeIcon className="size-3 shrink-0 text-orange-500" aria-hidden="true" />
      {label ?? procurosOnlyText}
    </span>
  );

  // Without a custom label the badge already reads "Only visible to Procuros members"; a tooltip
  // repeating it back would be noise.
  if (!label) {
    return badge;
  }

  return (
    <Tooltip>
      <TooltipTrigger>{badge}</TooltipTrigger>
      <TooltipContent>{procurosOnlyText}</TooltipContent>
    </Tooltip>
  );
};
